// Catalog.ID Client — Log Out
// ============================================================================
// The world both device-session flows act on, in one place.
//
// Logging out is MODULE state, not the Settings pane's subject, so the verb
// itself lives in the module's gear menu (`cid.log-out`, register-pane-types)
// rather than as a button inside Settings — shell USAGE.md "Where a button
// belongs". Settings keeps only "Forget this device", which is about THIS
// device's enrolments, and it builds its dependencies from here so the two
// flows cannot clear different things.
//
// `device-session.ts` holds the sequences themselves and is deliberately
// DOM-free; this module is the wiring that hands it the real browser world.

import * as accountApi from '../api/account';
import * as deviceApi from '../api/device';
import { listStoredSessions } from '../api/client';
import * as deviceStorage from '../state/device-storage';
import { session } from '../state/session';
import { identity } from '../state/identity';
import { keys } from '../state/keys';
import { resetOrgKeyCache } from '../state/org-keys';
import { clearSessionGrants, clearAllGrants } from '../key-grant/grant-policy';
import { clearAllModules } from '../key-grant/module-registry';
import { clearWalletSaveStash } from '../key-grant/bitcash-wallets-save-store';
import { logoutAllIdentities, type DeviceSessionDeps } from './device-session';

/**
 * Every dependency `logoutAllIdentities` / `forgetThisDevice` need, bound to
 * the live browser state. Only `clearIdentityGrants` distinguishes the two
 * flows: a plain log out never calls it.
 */
export function buildSessionDeps(): DeviceSessionDeps {
    return {
        listStoredSessions,
        listEnrolledIdentities: deviceStorage.listEnrolledIdentities,
        getDeviceToken: deviceStorage.getDeviceToken,
        logout: accountApi.logoutWithToken,
        removeDevice: deviceApi.removeDeviceWithToken,
        clearDeviceStorage: deviceStorage.clearDeviceStorage,
        clearIdentityGrants: (cid: string) => {
            clearAllGrants(cid);
            clearAllModules(cid);
        },
        clearInMemoryState: () => {
            keys.clear();
            // The unwrapped org key is derived from the delegate's own key, so it
            // must not outlive it.
            resetOrgKeyCache();
            // Audit C2: clear ephemeral Key Grant state so an in-flight save flow
            // and any session-scoped consent decisions don't outlive the logout.
            // Persistent "Allow always" grants and module registrations are the
            // user's explicit cross-session choices and survive a plain logout.
            clearSessionGrants();
            // The bitcash wallet stash (cid-encrypted-storage §7.3) — the only
            // in-flight save left after M6b retired the generic `save-prepare`
            // and its P-256 store. Its secret must not outlive the member whose
            // account it was minted under.
            clearWalletSaveStash();
            // Drops every stored session token and the active cid.
            session.clear();
            identity.clear();
        },
    };
}

/**
 * End the sessions of every identity on this device and return to the login
 * page. Enrolments are kept, so the next login here is password (+ PIN) as the
 * printed credentials page promises — de-enrolling is "Forget this device".
 *
 * The caller confirms first (the gear-menu action does); this does not ask.
 */
export async function logOutEverywhere(): Promise<void> {
    await logoutAllIdentities(buildSessionDeps());
    window.location.href = '/';
}
